"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";

const NAV_LINKS = [
  { label: "Home", href: "/homepage" },
  { label: "About", href: "/about" },
  { label: "Programs", href: "/programs" },
  { label: "Impact", href: "/impact" },
  { label: "Store", href: "/store" },
  { label: "Get Involved", href: "/get-involved" },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50"
      aria-label="Main navigation"
      style={{
        background: scrolled
          ? "rgba(10, 26, 18, 0.97)"
          : "rgba(10, 26, 18, 0.88)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: scrolled
          ? "1px solid rgba(201, 168, 76, 0.25)"
          : "1px solid rgba(201, 168, 76, 0.12)",
        boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.35)" : "none",
        transition: "all 0.4s ease",
      }}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <AppLogo
            size={56}
            text="Dr. W. T. Gwenigale Foundation"
            iconName="HeartIcon"
          />
        </Link>

        {/* Desktop nav links */}
        <div className="hidden lg:flex items-center gap-5">
          {NAV_LINKS?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={link?.label}
                href={link?.href}
                className="text-sm font-body font-medium transition-colors duration-200"
                style={{
                  color: isActive ? "var(--color-gold-light)" : "rgba(255,255,255,0.65)",
                }}
              >
                {link?.label}
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/donate"
            className="btn-primary"
            style={{ padding: "0.6rem 1.25rem", fontSize: "0.875rem" }}
          >
            <Icon name="HeartIcon" size={16} variant="solid" />
            Donate Now
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          <Icon name={menuOpen ? "XMarkIcon" : "Bars3Icon"} size={22} />
        </button>
      </nav>
      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="lg:hidden overflow-hidden shadow-2xl"
          style={{
            background: "rgba(10, 26, 18, 0.98)",
            backdropFilter: "blur(20px)",
            borderTop: "1px solid rgba(201,168,76,0.15)",
          }}
        >
          <div className="flex flex-col p-4 gap-1 max-w-7xl mx-auto">
            {NAV_LINKS?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.label}
                  href={link?.href}
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-3 rounded-xl font-body font-medium text-base hover:bg-white/8 transition-all"
                  style={{ color: isActive ? "var(--color-gold-light)" : "rgba(255,255,255,0.75)" }}
                >
                  {link?.label}
                </Link>
              );
            })}
            <div className="pt-3 border-t border-white/10 mt-2">
              <Link
                href="/donate"
                onClick={() => setMenuOpen(false)}
                className="btn-primary w-full justify-center"
              >
                <Icon name="HeartIcon" size={18} variant="solid" />
                Donate Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}